import React, { useState, useRef, useEffect, Fragment } from 'react';
import {
  useChatsMutation,
  useGroupParticipantMutation,
  useSendMessageMutation,
  useWhatsAppProfileMutation
} from '../api/chatApi';

import {
  encryptUrlSafe,
  formatePhoneNumber,
  formatTimestamp,
  formatWhatsAppMessage,
  getDateLabel,
  getDisplayName,
  parseVCard,
  whatsappMediaType
} from '../../../utils/helpers';

import { useSocket } from '../../../contexts/SocketContext';
import Toast from '../../../components/ToastNotification';
// import { DEFAULT_PAGE_SIZE } from '../../../utils/contants';
import TickIcon from '../../../assets/icons/TickIcon';
import Reaction from './Reaction';
import Participant from './Participant';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../../components/Loader';
import MessageOptions from './MessageOptions';
import PinDurationModal from './PinDurationModal';
import ScheduleMessage from './ScheduleMessage';
import { useNavigate } from 'react-router-dom';
import { setScheduledMessageCount } from '../slices/chatSlice';
import { useGetScheduleMessageCountMutation } from '../../scheduleMessages/api/scheduleMessageApi';

const normalizeEmoji = (emoji) => emoji.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');

const Chat = ({ selectedId, contactState }) => {
  const { session } = useSelector((state) => state.auth);
  const {scheduleMessageCount} = useSelector(state => state.chat);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [hasMore, setHasMore] = useState(false);

  const [messages, setMessages] = useState([]);
  const [participantsList, setParticipantsList] = useState([]);
  const [input, setInput] = useState('');
  const [media, setMedia] = useState(null);
  const [overlay, setOverlay] = useState(true);
  const [preview, setPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [participantModal, setParticipantModal] = useState(false);
  const [selectedReactionMessage, setSelectedReactionMessage] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [pinMessagesList, setPinMessagesList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [isReply, setIsReply] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(false);

  const messageRefs = useRef({});
  const chatRef = useRef(null);
  const isRestoring = useRef(false);
  const prevMessageCount = useRef(0);
  const inputRef = useRef(null);
  const socket = useSocket();
  const { selectContact, setSelectContact } = contactState;
  const navigate = useNavigate();

  const [chatsApi] = useChatsMutation();
  const [sendMessageApi] = useSendMessageMutation();
  const [whatsAppUserProfileApi] = useWhatsAppProfileMutation();
  const [groupParticipantApi] = useGroupParticipantMutation();
  const [scheduledMessageCountApi] = useGetScheduleMessageCountMutation();
  const dispatch = useDispatch();

  let lastDateLabel = null;

  const groupReactions = (reactions = []) => {
    const groups = {};
    for (const reaction of reactions) {
      const baseEmoji = normalizeEmoji(reaction.emoji);
      if (!groups[baseEmoji]) groups[baseEmoji] = [];
      groups[baseEmoji].push(reaction);
    }
    return groups;
  };

  const getBlankSpaceWidth = (msg) => {
    const sender = session?.client_id === msg.sender;
    if (sender) return msg.is_edited && msg.status !== 1 ? "120px" : msg.is_pinned ? "110px" : "90px";
    return msg.is_edited ? "105px" : msg.is_pinned ? "90px" : "70px";
  };

  const fetchChats = async (isLoadMore = false) => {
    try {
      setIsLoader(true);
      const response = await chatsApi({
        chat_id: selectContact?.wa_id,
        user_id: selectedId,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize
      }).unwrap();

      // setOverlay(false);

      const {chats: newMessages, pinMessages} = response.data;

      if (pinMessages) {
        setPinMessagesList(pinMessages);
        setSelected(pinMessages[pinMessages.length - 1]);
      }

      if (newMessages.length === 0) {
        setHasMore(false);
        return;
      }

      setHasMore(newMessages.length === pagination.pageSize);
      setMessages(prev =>
        isLoadMore ? [...newMessages, ...prev] : newMessages
      );

    } catch (error) {
      console.error('Failed to fetch chats:', error);
      setMessages([]);
      setPinMessagesList([]);
    } finally {
      setIsLoader(false);
      setOverlay(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await whatsAppUserProfileApi({
        chat_id: selectContact?.wa_id,
        user_id: selectedId
      }).unwrap();

      if (response.data?.profile) {
        setProfilePic(response.data.profile);
      } else setProfilePic("/avatar.jpeg");
    } catch {
      setProfilePic(null);
    }
  };

  const fetchGroupParticipant = async (selectContact) => {
    if (!selectContact?.is_group) return;

    try {
      const participant = await groupParticipantApi({
        user_id: selectedId,
        group_id: selectContact?.wa_id
      }).unwrap();

      const participantList = participant?.data;
      if (participantList) {
        setParticipantModal(true);
        setParticipantsList(participantList);
      }
    } catch {
      setParticipantsList([]);
    }
  };

  const resetTextareaHeight = () => {
    // const textarea = document.querySelector('textarea');
    // if (textarea) textarea.style.height = '50px';
    if (inputRef.current) {
      inputRef.current.style.height = '50px';
    }
  };

  const fetchScheduledMessageCount = async () => {
    try {
      const response = await scheduledMessageCountApi({
        userId: selectedId,
        remoteJid: selectContact?.wa_id
      }).unwrap();

      dispatch(setScheduledMessageCount(response?.data?.count))
    } catch (error) {
      Toast.error('Error comes in fetchScheduledMessageCount', error.message);
    }
  }

  // Socket message listeners (unchanged)
  useEffect(() => {
    if (!socket || !selectContact) return;

    const handleIncomingMessage = ({ employeeId, msgObj: message }) => {
      if (employeeId !== selectedId) return;

      const chatId = selectContact?.wa_id;
      const isGroup = message?.is_group && message?.recipient === chatId;
      const isMine = message?.is_from_me && message?.recipient === chatId;
      const isOther = !message?.is_from_me && message?.sender === chatId && !message?.is_group;

      if (isGroup || isMine || isOther) {
        setMessages(prev => [...prev, message]);
      }
    };

    const handleSeenMessage = ({ employeeId, seenMessages }) => {
      if (employeeId !== selectedId) return;

      setMessages(prev =>
        prev.map(msg => {
          const seenMsg = seenMessages.find(s => s.message_id === msg.message_id);
          return seenMsg ? { ...msg, status: seenMsg.status } : msg;
        })
      );
    };

    const handleMessageReaction = ({ employeeId, reactionRecord: reaction }) => {
      if (employeeId !== selectedId) return;

      setMessages(prev =>
        prev.map(msg => {
          if (msg.message_id === reaction.target_msg_id) {
            const idx = msg?.reactions?.findIndex(r => r.reactor_jid === reaction.reactor_jid);
            const updated = [...msg?.reactions];

            if (idx !== -1) {
              if (!reaction.emoji) updated.splice(idx, 1);
              else updated[idx] = reaction;
            } else if (reaction.emoji) {
              updated.push(reaction);
            }

            return { ...msg, reactions: updated };
          }
          return msg;
        })
      );
    };

    const handleEditMessage = ({ employeeId, editMessage }) => {
      if (employeeId !== selectedId) return;

      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === editMessage.message_id
            ? { ...msg, content: editMessage.content, is_edited: true }
            : msg
        )
      );
    };

    const handleGroupParticipantUpdate = ({ employeeId, groupId, participants, action }) => {
      if (employeeId !== selectedId || selectContact?.wa_id !== groupId) return;

      if (action === 'add') {
        setParticipantsList(prev => [
          ...prev,
          ...participants.map(p => ({
            participant_jid: p,
            status: 'add',
            is_admin: false,
            is_superadmin: false
          }))
        ]);
      }

      if (action === 'remove') {
        setParticipantsList(prev =>
          prev.map(p =>
            participants.includes(p.participant_jid)
              ? { ...p, status: 'remove' }
              : p
          )
        );
      }
    };

    const handlePinUnMessage = ({ employeeId, pinMessage }) => {
      if (employeeId !== selectedId) return;

      const {is_pinned, pinned_by, pinned_at} = pinMessage;

      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === pinMessage.message_id
            ? { ...msg, is_pinned, pinned_by, pinned_at }
            : msg
        )
      );

      setPinMessagesList(prev => {
        if (is_pinned) {
          return [...prev, pinMessage]
        } 

        return prev.filter(m => m.message_id !== pinMessage.message_id);
      })
    }

    socket.on('message-received', handleIncomingMessage);
    socket.on('seen-message', handleSeenMessage);
    socket.on('message-reaction', handleMessageReaction);
    socket.on('edit-message', handleEditMessage);
    socket.on('group-participant-update', handleGroupParticipantUpdate);
    socket.on('pin-message', handlePinUnMessage);
    socket.on('unpin-message', handlePinUnMessage);

    return () => {
      socket.off('message-received', handleIncomingMessage);
      socket.off('seen-message', handleSeenMessage);
      socket.off('message-reaction', handleMessageReaction);
      socket.off('edit-message', handleEditMessage);
      socket.off('group-participant-update', handleGroupParticipantUpdate);
      socket.off('pin-message', handlePinUnMessage);
      socket.off('unpin-message', handlePinUnMessage);
    };
  }, [socket, selectContact]);

  useEffect(() => {
    if (selectContact) {
      setPagination({ ...pagination, pageIndex: 0 });
      setHasMore(false);
      setParticipantModal(false);
      // fetchChats(false);
      setMessages([]);
      fetchUserProfile();
      fetchScheduledMessageCount();
    }
  }, [selectContact]);

  useEffect(() => {
    if (pagination.pageIndex === 0 && selectContact) {
      fetchChats(false);
    } else if (pagination.pageIndex !== 0 && hasMore) {
      fetchChats(true);
    }
  }, [pagination])

  useEffect(() => {
    if (!chatRef.current) return;
    const container = chatRef.current;
    
    const handleScroll = async () => {
      if (container.scrollTop <= 100 && hasMore && !isLoader && !isRestoring.current) {
        const oldHeight = container.scrollHeight;
        
        isRestoring.current = true;
        container.dataset.oldHeight = oldHeight;
        
        setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex + 1 }));

        // setTimeout(() => {
        //   container.scrollTop = container.scrollHeight - oldHeight;
        // }, 100);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, isLoader]);

  useEffect(() => {
    if (!isRestoring.current || !chatRef.current) return;
  
    const container = chatRef.current;
    const oldHeight = Number(container.dataset.oldHeight);
  
    setTimeout(() => {
      const newHeight = container.scrollHeight;
      container.scrollTop = newHeight - oldHeight;
      isRestoring.current = false;
    }, 0);
  }, [messages]);

  

  // Auto-scroll to bottom for new messages
  // useEffect(() => {
  //   if (chatRef.current) {
  //     chatRef.current.scrollTop = chatRef.current.scrollHeight;
  //   }
  // }, [messages]);

  useEffect(() => {
    if (!chatRef.current) return;
    const container = chatRef.current;
  
    // Don't auto-scroll when restoring pagination
    if (isRestoring.current) return;
  
    const isNewMessageAppend =
      messages.length >= prevMessageCount.current;

    prevMessageCount.current = messages.length;
    // Only scroll on new messages, not when loading old ones
    if (isNewMessageAppend) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!viewModal) return;
  
    const handleOutsideClick = () => setViewModal(false);
    document.addEventListener("click", handleOutsideClick);
  
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [viewModal]);

  const handlChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (preview) URL.revokeObjectURL(preview);

    setMedia(selected);
    setMediaType(selected.type);

    if (
      selected.type.startsWith('image/') ||
      selected.type.startsWith('video/') ||
      selected.type.startsWith('audio/')
    ) {
      setPreview(URL.createObjectURL(selected));
    } else {
      setPreview(null);
    }
  };

  const sendMessage = async () => {
    const hasText = input.trim();
    const hasMedia = Boolean(media);

    if (!hasText && !hasMedia) {
      Toast.error('Please enter text...')
      return;
    }

    if (media?.size > 104857600){
      Toast.error('Please select media size less than 100 MB')
      return;
    }

    const formData = new FormData();
    formData.append('recipientJid', selectContact?.wa_id);
    formData.append('id', selectedId);
    if (selectedScheduleDate) {
      formData.append('schedule_date_time', new Date(selectedScheduleDate).toISOString());
    }

    setInput('');
    setMedia(null);
    setPreview(null);
    resetTextareaHeight();
    setSelectedScheduleDate(null);

    if (hasMedia) {
      formData.append(
        'type',
        mediaType.startsWith('image/')
          ? 'image'
          : mediaType.startsWith('video/')
          ? 'video'
          : mediaType.startsWith('audio/')
          ? 'audio'
          : 'document'
      );
      
      formData.append('file', media);
      formData.append('caption', hasText);
    } else {
      formData.append('type', 'text');
      formData.append('content', hasText);
    }

    if (isReply) {
      formData.append('reply_msg', JSON.stringify(selectedMessage));
    }

    try {

      const res = await sendMessageApi(formData).unwrap();
      Toast.success(res.message);
      setIsReply(false);
    } catch (err) {
      Toast.error(err?.data?.message || 'Failed to send message');
    }
  };

  const getMediaUrls = (fileName, fileType, createdAt, filePath) => {
    if (!fileName) return null;

    const base = process.env.REACT_APP_UPLOAD_FILE_PATH;
    const cleanDate = createdAt?.split("T")[0];
    const [year, month, day] = cleanDate?.split("-");

    // The stored path wins when present: bulk upload media is one shared file
    // whose folder date can differ from each message's date.
    const storedPathUrl = filePath?.startsWith('uploads/')
      ? `${base}/${filePath.slice('uploads/'.length)}`
      : null;

    return [
      storedPathUrl,
      `${base}/whatsapp_media/${year}/${month}/${day}/${whatsappMediaType[fileType]}/${fileName}`,
      `${base}/${fileName}`
    ].filter(Boolean);
  };

  const MediaWithFallback = ({ urls, type, content, filename, media_expired }) => {
    const [index, setIndex] = useState(0);
    const mediaSizeExceed = content === '' && filename === '';
    
    if (media_expired) {
      return (
        <div className="media-error">
          ⚠️ This media has expired and is no longer available.
        </div>
      );
    } else if (mediaSizeExceed) {
      return (
        <div className="media-error">
          ⚠️ Attachment exceeds the size limit.
        </div>
      );
    }

    if (!Array.isArray(urls) || urls.length === 0) return null;
    const src = urls[index];

    const handleError = () => {
      if (index < urls.length - 1) {
        setIndex(index + 1);
      }
    };
  
    if (type === "image") {
      return (
        <a href={src} target="_blank" rel="noopener noreferrer">
          <img
            src={src}
            onError={handleError}
            alt="img"
            style={styles.mediaPreview}
          />
        </a>
      );
    }
  
    if (type === "audio") {
      return (
        <audio
          controls
          style={styles.audio}
          src={src}
          onError={handleError}
        />
      );
    }
  
    if (type === "video") {
      return (
        <video
          controls
          style={styles.video}
          src={src}
          onError={handleError}
        />
      );
    }

    if (type === "document") {
      return (
        <a href={src} target="_blank" rel="noopener noreferrer" className="doc-link">
          📄 {filename}
        </a>
      );
    }
  
    return null;
  };
  
  const renderMessageContent = (msg) => {
    // — FULL ORIGINAL CONTENT RENDERER KEPT EXACTLY AS PROVIDED —
    // const mediaUrl = msg.file_name ? `${process.env.REACT_APP_UPLOAD_FILE_PATH}/${msg.file_name}` : null;
    const {file_name, file_path, type, content, createdAt, media_expired} = msg;
    const mediaUrls = getMediaUrls(file_name, type, createdAt, file_path);

    // Media captions are stored for bulk upload messages; other media keeps content ''.
    const withCaption = (media) => (
      content ? (
        <>
          {media}
          <span
            className="media-caption"
            style={{ display: 'block', marginTop: 6 }}
            dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(content) }}
          />
        </>
      ) : media
    );

    switch (msg.type) {
      case 'conversation':
      case 'extendedTextMessage':
        return (
          <span dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(content) }} />
        );

      case 'imageMessage':
        return withCaption(<MediaWithFallback urls={mediaUrls} type="image" content={content} filename={file_name} media_expired={media_expired}/>);

      case 'audioMessage':
        return withCaption(<MediaWithFallback urls={mediaUrls} type="audio" content={content} filename={file_name} media_expired={media_expired}/>);

      case 'videoMessage':
        return withCaption(<MediaWithFallback urls={mediaUrls} type="video" content={content} filename={file_name} media_expired={media_expired}/>);

      case 'documentMessage':
      return withCaption(<MediaWithFallback urls={mediaUrls} type="document" content={content} filename={file_name} media_expired={media_expired}/>);

      case 'locationMessage':
        try {
          const location = typeof content === 'string' ? JSON.parse(content) : content;
          const { latitude, longitude, description } = location;

          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

          return (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={styles.locationCard}>
              <div>📍 <strong>{description || 'Location Shared'}</strong></div>
              <div style={{ fontSize: '13px', color: '#555' }}>{latitude}, {longitude}</div>
            </a>
          );
        } catch {
          return <div style={styles.locationCard}>📍 Invalid Location</div>;
        }

      case 'contactMessage': {
        let contact = {};
        try { contact = JSON.parse(content); } catch {}

        const vcard = contact.vcard || '';
        const { name, phone } = parseVCard(vcard);
        const blob = new Blob([vcard], { type: 'text/vcard' });
        const vcfUrl = URL.createObjectURL(blob);

        return (
          <div style={{ padding: '5px' }}>
            <div>👤 <strong>{name}</strong> - {phone}</div>
            <a href={vcfUrl} download={`${name}.vcf`} style={{ color: 'blue' }}>
              📥 Download Contact
            </a>
          </div>
        );
      }

      default:
        return <span>Unsupported type: {type}</span>;
    }
  };

  const renderReview = () => {
    if (!media) return null;

    if (mediaType?.startsWith('image/')) {
      return <img src={preview} alt="Preview" />;
    }

    if (mediaType?.startsWith('video/')) {
      return (
        <video controls style={styles.video}>
          <source src={preview} type={mediaType} />
        </video>
      );
    }

    if (mediaType?.startsWith('audio/')) {
      return (
        <audio controls style={styles.audio}>
          <source src={preview} type={mediaType} />
        </audio>
      );
    }

    return (
      <div style={styles.doc}>
        <strong>📄 File: {media.name}</strong>
      </div>
    );
  };

  const viewReactions = (reactions) => {
    setSelectedReactionMessage(reactions);
    setModalOpen(true);
  };

  const handleScheduleDateTime = () => {
    setScheduleModal(true);
    setSelectedScheduleDate(null);
  }

  const handleChatWindow = () => {
    setSelectContact(null);
    setProfilePic(null);
    setOverlay(true);
    setMessages([]);
    setParticipantModal(false);
    setPagination({ ...pagination, pageIndex: 0 });
    setHasMore(false);
  };

  const generateSenderName = (contact) => {
    if (!contact) return "Unknown";

    const { name, push_name, wa_id } = contact;
    const startsWithNumber = (value) => /^\d/.test(value);

    if (!startsWithNumber(name)) return name;

    return `${wa_id?.split('@')[0]} ${push_name ? `~ ${push_name}` : ''}`;
  };

  const highlightHandler = () => {
    const target = messageRefs.current[selected.message_id]?.parentNode?.parentNode;
    if (!target) {
      // setTimeout(() => {
      //   setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
      // }, 100)
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('highlight');
    setTimeout(() => target.classList.remove('highlight'), 1000);

    const currentIndex = pinMessagesList.findIndex(msg => msg.id === selected.id);
    
    const nextIndex = (currentIndex + 1) % pinMessagesList.length;
    setSelected(pinMessagesList[nextIndex]);
  }

  

  const openMenuHandler = (e, msg) => {
    e.stopPropagation();

    const {right, bottom} = e.target.getBoundingClientRect();
    let pos = { x: right + 10, y: bottom -20 };
    if(e.pageY > 650) {
      pos = { x: right + 10, y: bottom -80 }
    }

    setPosition(pos);
    setViewModal(true);
    setSelectedMessage(msg);
  }

  return (
    <div className="chat-window">
      <div style={styles.chatContainer}>

        {showFullImage && (
          <div className="overlay" onClick={() => setShowFullImage(false)}>
            <img src={profilePic} alt="Expanded Profile" className="expanded-img" />
          </div>
        )}

        {overlay ? (
          <div className="chat-overlay">
            <img src="/chat.png" alt="" />
            <h2>Ready to Chat</h2>
          </div>
        ) : (
          <>
            {selectContact && (
              <div className="userInfo">
                <div className="info_card">
                  <div className="card_img">
                    <img src={profilePic} alt="" onClick={() => setShowFullImage(true)} />
                  </div>

                  <div onClick={() => fetchGroupParticipant(selectContact)}>
                    <p>{getDisplayName(selectContact)}</p>
                    <p>{formatePhoneNumber(selectContact?.wa_id)}</p>
                  </div>
                </div>

                <div className="chat_close">
                  <button onClick={handleChatWindow}>
                    <img src="/close.png" alt="" />
                  </button>
                </div>
              </div>
            )}

            {isLoader && <Loader />}

            {pinMessagesList.length > 0 && (
              <div className='pinned-wrapper'>
                <div className='pinned-section'>
                  <div className='btn-wrapper'>
                    {pinMessagesList.length > 1 && pinMessagesList.map((msg) => (
                      <span
                        key={msg.id}
                        className={`pin-btn ${selected.id === msg.id ? "actived" : ""}`}
                        onClick={() => {
                          setSelected(msg);
                          highlightHandler()
                        }}
                      >
                        -
                      </span>
                    ))}
                  </div>
                  <div className='pinned-section-icon'>
                    <img className='pin-img' src="/pin.png" alt=''/>
                  </div>
                  <span 
                    className="pin-text" 
                    onClick={highlightHandler}
                    >
                  
                    {selected 
                      ? (() => {
                        const message = pinMessagesList.find(m => m.id === selected.id);
                        const contactName = message?.contact?.name;

                        const isSenderCurrentUser = selected.sender?.split('@')[0] === session?.phone_number;
                        const displayName = isSenderCurrentUser ? "You" : contactName;

                        const contentPreview =
                          selected.content.length > 10
                            ? selected.content.slice(0, 10) + "..."
                            : selected.content;
                        
                        // return `${displayName}: ${contentPreview}`;
                        return (
                          <>
                            <span className="pin-name">{displayName}</span>
                            <span className="pin-separator">: </span>
                            <span className="pin-content">{contentPreview}</span>
                          </>
                        );
                      })() 
                      : "No pin selected"
                    }
                  </span>
                </div>
                {/* <div className='carret-down-arrow-div'>
                  <img className='down-arrow-icon' src="/down-arrow.png" alt=""/>
                </div> */}
              </div>
            )}
            <div style={styles.messageArea} ref={chatRef} className="scrollContainer">
              {messages.map((msg, index) => {
                const currentLabel = getDateLabel(msg.timestamp);
                const showLabel = currentLabel !== lastDateLabel;
                const sender = session?.client_id === msg.sender;
                
                lastDateLabel = currentLabel;

                const prevMsg = index > 0 ? messages[index - 1] : null;
                const prevDateLabel = prevMsg ? getDateLabel(prevMsg.timestamp) : null;
                const dateChanged = currentLabel !== prevDateLabel;
                const isFirstOfGrp = msg.is_group && !sender && (dateChanged || !prevMsg || prevMsg.sender !== msg.sender);

                return (
                  <Fragment key={index}>
                    {showLabel && (
                      <div className="date_label">
                        <span>{currentLabel}</span>
                      </div>
                    )}

                    <div style={{ textAlign: sender ? 'right' : 'left' }}>
                      <div
                        style={{
                          ...styles.messageBubble,
                          background: sender ? '#dcf8c6' : '#ffffff',
                          position: 'relative'
                        }}
                        className='message-wrapper'
                      >
                        {isFirstOfGrp && (
                          <div className="grp-sender">
                            <p className="name">
                              {msg.contact
                                ? generateSenderName(msg.contact)
                                : msg.sender?.split('@')[0]}
                            </p>
                            
                          </div>
                        )}

                        <div className='reply-options'>
                          <img 
                            className='down-arrow-icon'
                            style={{background: sender ? '#dcf8c6' : '#ffffff',}}
                            src="/down-arrow.png" alt="" 
                            onClick={(e) => openMenuHandler(e, msg)}
                          />
                        </div>

                        <div
                          ref={el => { if (el) messageRefs.current[msg.message_id] = el; }}
                          className={`content ${msg.file_name ? 'media' : ''}`}
                        >
                          {msg.replied_message_id && msg.quoted_text && (
                            <div
                              className="quoted-message-box"
                              onClick={() => {
                                const target = messageRefs.current[msg.replied_message_id]?.parentNode?.parentNode;
                                if (target) {
                                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  target.classList.add('highlight');
                                  setTimeout(() => target.classList.remove('highlight'), 1500);
                                }
                              }}
                            >
                              <div className="quoted-content">
                                <p className="quoted-sender">
                                  {msg.replied_to !== session?.client_id
                                    ? formatePhoneNumber(msg.replied_to?.split('@')[0])
                                    : 'You'}
                                </p>
                                <p className="quoted-text">
                                  {msg.quoted_text.length > 100
                                    ? msg.quoted_text.slice(0, 100) + '...'
                                    : msg.quoted_text}
                                </p>
                              </div>
                            </div>
                          )}

                          {renderMessageContent(msg)}
                          <span className="msg-blank-space" style={{ width: getBlankSpaceWidth(msg) }}></span>
                        </div>

                        <div style={styles.timestamp} className="time-reaction">
                          {msg.is_pinned && <div className='pinned-icon'><img className='pin-img' src="/pin.png" alt=''/></div>}
                          {msg.is_edited && <p>Edited</p>}
                          <p>{formatTimestamp(msg.timestamp)}</p>
                          {sender && msg.status !== 1 && <TickIcon status={msg.status} />}
                        </div>
                      </div>

                      {msg?.reactions?.length > 0 && (
                        <div
                          className={`reaction-bubble ${sender ? 'right' : 'left'}`}
                          onClick={() => viewReactions(msg.reactions)}
                        >
                          {Object.entries(groupReactions(msg.reactions)).map(([emoji]) => (
                            <span className="reaction-item" key={emoji}>{emoji}</span>
                          ))}
                          {msg.reactions.length > 1 && msg.reactions.length}
                        </div>
                      )}
                    </div>
                  </Fragment>
                );
              })} 
            </div>
              {media && (<div className="review">
                <button onClick={() => { setMedia(null); setPreview(null); }}>
                  <img src="/close.png" alt="" />
                </button>
              {renderReview()}
            </div>)}

            <div className="input-section">
              {isReply && (
                <div className='reply-bubble'>
                  <img className="close-icon" src="/close.png" alt="" onClick={() => setIsReply(false)} />
                  <p className={`${session?.client_id === selectedMessage.sender ? 'blue-text' : 'brown-text'}`}>
                    {selectedMessage.contact
                      && session?.client_id === selectedMessage.sender 
                      ? 'You' 
                      : generateSenderName(selectedMessage.contact)
                    }
                  </p>
                  <p>
                    {selectedMessage.content
                      ? selectedMessage.content.length > 300
                        ? selectedMessage.content.substring(0, 300) + "..."
                        : selectedMessage.content
                      : selectedMessage.type || "Attachment"}
                  </p>
                </div>
              )}
              <div className='input-wrapper-main'>
                <div style={{marginLeft: '30px'}}>
                  {!selectContact?.is_group && scheduleMessageCount > 0 && <p>{`${scheduleMessageCount} messages scheduled to be sent to ${selectContact?.push_name || selectContact?.name}. `}
                    <span style={{color: "rgb(20, 86, 184)", cursor:'pointer '}} onClick={() => navigate(`/schedule-message/${encryptUrlSafe(selectedId)}`)}>
                      <b>See all scheduled messages</b>
                    </span>
                  </p>}
                </div>
                <div className='input-wrapper'>
                  <input
                    type="file"
                    accept="image/*,video/*,audio/*,application/*,.csv,text/csv"
                    onChange={handlChange}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" style={styles.uploadButton}>+</label>

                  <textarea
                    ref={inputRef}
                    placeholder={`Message ${selectContact?.push_name || selectContact?.name}`}
                    value={input}
                    rows={1}
                    onChange={(e) => {
                      setInput(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height = `${Math.max(e.target.scrollHeight, 50)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    style={styles.textInput}
                  />

                  <div className='ca-datepicker'>
                    <button title='Schedule Date Time' className="send-btn" onClick={handleScheduleDateTime}>
                      <img src="/timetable.png" alt="Send" />
                    </button>
                  </div>

                  <button onClick={sendMessage} className="send-btn">
                    <img src="/send.png" alt="Send" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {isModalOpen && (
          <Reaction
            message={selectedReactionMessage}
            onClose={() => {
              setSelectedReactionMessage(null);
              setModalOpen(false);
            }}
          />
        )}
      </div>

      {participantModal && (
        <Participant
          participants={participantsList}
          setParticipantModal={setParticipantModal}
        />
      )}

      {viewModal && (
        <MessageOptions 
          onClose={() => setViewModal(false)}
          position={position}
          selectedMessage={selectedMessage}
          setSelectContact={setSelectContact}
          onOpenPinDurationModal={() => setShowPinModal(true)}
          replyState={() => setIsReply(true)}
        />
      )}

      {showPinModal && (
        <PinDurationModal 
          onClose={() => setShowPinModal(false)}
          selectedMessage={selectedMessage}
        />
      )}

      {scheduleModal && (
        <ScheduleMessage 
          onClose={() => setScheduleModal(false)}
          dateState= {{ selectedDate: selectedScheduleDate, setSelectedDate: setSelectedScheduleDate }}
          scheduleMessage={() => sendMessage()}
          updateScheduleCount={() => fetchScheduledMessageCount()}
        />
      )}
    </div>
  );
};

const styles = {
  chatContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    background: "#f9f9f9",
    borderTop: "0.5px solid #f0f0f0"
  },
  messageArea: {
    padding: "15px",
    overflowY: "auto",
    height: "90%",
    backgroundImage: 'url("/images/Frame2.svg")',
    backgroundPosition: "center"
  },
  messageBubble: {
    display: "inline-block",
    padding: "10px",
    borderRadius: "8px",
    margin: "5px 0",
    maxWidth: "65%",
    wordBreak: "break-word",
    background: "#fff"
  },
  mediaPreview: {
    // minWidth: "150px",
    width: "100%",
    height: "250px",
    // maxHeight: "150px",
    marginTop: "5px",
    borderRadius: "6px",
    objectFit: "cover"
  },
  audio: {
    marginTop: "5px",
    width: "100%"
  },
  video: {
    marginTop: "5px",
    // minWidth: "150px",
    width: "100%",
    height: "200px",
    borderRadius: "6px"
  },
  doc: {
    marginTop: "5px",
    width: "100%",
    minHeight: "50px",
    borderRadius: "6px",
    backgroundColor: "#fff",
    placeContent: "center"
  },
  textInput: {
    flex: 1,
    padding: "16px 12px",
    fontSize: "16px",
    borderRadius: "16px",
    border: "none",
    marginRight: "60px",
    outline: "none",
    background: "rgb(224, 247, 250)",
    resize: "none",
    lineHeight: "20px",
    maxHeight: "200px",
    overflowY: "auto"
  },
  uploadButton: {
    width: "40px",
    height: "40px",
    fontSize: "30px",
    cursor: "pointer",
    marginRight: "10px",
    borderRadius: "15px",
    display: "flex",
    background: "#e0f7fa",
    alignItems: "center",
    justifyContent: "center"
  },
  timestamp: {
    fontSize: "12px",
    color: "#666",
    display: "flex",
    gap: "4px",
    width: "100%",
    justifyContent: "flex-end"
  },
  locationCard: {
    background: "#e8f5e9",
    borderLeft: "5px solid #43a047",
    padding: "8px",
    borderRadius: "6px",
    marginTop: "5px",
    display: "block",
    color: "inherit",
    textDecoration: "none"
  }
};

export default Chat;
